/**
 * AuthContext — the single source of truth for who is signed in and what they
 * may do.
 *
 * Why this exists
 * ---------------
 * The previous shell held `useState('admin')` and rendered two buttons that let
 * any visitor toggle their own role. Role was a client-side variable, so every
 * "permission check" in the app was decorative. This provider replaces that with
 * a server-authoritative session:
 *
 *   - The token is the only thing read from storage on boot.
 *   - The *user* is always re-fetched from `/auth/me` before it is trusted. The
 *     cached copy in sessionStorage is used purely to avoid a flash of the login
 *     screen during that round trip, and is discarded the moment the server
 *     answers (or refuses).
 *   - `permissions` comes from the server on every page load. There is no local
 *     fallback and no way to widen it from the client.
 *
 * A single 401 subscriber is registered here, so a token that dies mid-session
 * drops the whole app back to the sign-in screen from one place rather than each
 * component interpreting an error string.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import * as api from '../api'

/** Permission keys the backend ships in `user.permissions`. */
const PERMISSION_KEYS = [
  'manage_users',
  'manage_clients',
  'delete_clients',
  'view_audit_log',
  'generate_reports',
  'dispatch_reports',
  'manage_settings',
]

const EMPTY_PERMISSIONS = Object.freeze(
  PERMISSION_KEYS.reduce((acc, key) => {
    acc[key] = false
    return acc
  }, {}),
)

/**
 * Normalise a user payload from `/auth/login` or `/auth/me`.
 *
 * The permissions object is rebuilt from a known key list so an unexpected shape
 * (null, a string, a partial object) degrades to "no permissions" instead of
 * throwing inside a render.
 */
function normaliseUser(raw) {
  if (!raw || typeof raw !== 'object') return null

  const source = raw.permissions && typeof raw.permissions === 'object' ? raw.permissions : {}
  const permissions = PERMISSION_KEYS.reduce((acc, key) => {
    acc[key] = source[key] === true
    return acc
  }, {})

  return {
    id: raw.id ?? null,
    name: raw.name ?? '',
    email: raw.email ?? '',
    role: raw.role ?? '',
    role_label: raw.role_label ?? raw.role ?? '',
    is_admin: raw.is_admin === true,
    must_change_password: raw.must_change_password === true,
    permissions,
  }
}

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  // A cached user lets the shell paint immediately; `status` stays 'checking'
  // until the server confirms, so nothing privileged is actionable yet.
  const [user, setUser] = useState(() =>
    api.getToken() ? normaliseUser(api.getStoredUser()) : null,
  )
  const [status, setStatus] = useState(() => (api.getToken() ? 'checking' : 'anonymous'))
  const [error, setError] = useState(null)
  const [mustChangePassword, setMustChangePassword] = useState(false)

  // Guards against setState after unmount and against overlapping revalidations.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const resetToAnonymous = useCallback((message = null) => {
    api.clearSession()
    if (!mountedRef.current) return
    setUser(null)
    setMustChangePassword(false)
    setStatus('anonymous')
    setError(message)
  }, [])

  // ── Boot: revalidate any stored token against the server ───────────────────
  useEffect(() => {
    if (!api.getToken()) {
      setStatus('anonymous')
      return
    }

    let cancelled = false
    ;(async () => {
      try {
        const fresh = await api.fetchCurrentUser()
        if (cancelled || !mountedRef.current) return
        const next = normaliseUser(fresh)
        if (!next) {
          resetToAnonymous()
          return
        }
        // Keep the cache in step with what the server just told us.
        api.setSession(api.getToken(), fresh)
        setUser(next)
        setMustChangePassword(next.must_change_password)
        setStatus('authenticated')
        setError(null)
      } catch {
        if (cancelled) return
        // Expired, revoked or unreachable: either way the token is not usable.
        resetToAnonymous()
      }
    })()

    return () => {
      cancelled = true
    }
  }, [resetToAnonymous])

  // ── A 401 anywhere in the app ends the session ─────────────────────────────
  useEffect(() => {
    const unsubscribe = api.onUnauthorized(() => {
      if (!mountedRef.current) return
      setUser(null)
      setMustChangePassword(false)
      setStatus('anonymous')
      setError('Your session has expired. Sign in again to continue.')
    })
    return unsubscribe
  }, [])

  const signIn = useCallback(async (email, password) => {
    setError(null)
    const response = await api.login(email, password)

    const token = response?.token ?? response?.access_token
    if (!token) {
      throw new api.ApiError('Sign-in succeeded but no session token was issued.', { status: 0 })
    }

    api.setSession(token, response?.user ?? null)

    // The login payload carries `must_change_password`; capture it before /auth/me.
    const loginUser = normaliseUser(response?.user)
    const forcedRotation = response?.user?.must_change_password === true

    // /auth/me is the authority on permissions.
    let resolved = loginUser
    try {
      const fresh = await api.fetchCurrentUser()
      const next = normaliseUser(fresh)
      if (next) {
        resolved = next
        api.setSession(token, fresh)
      }
    } catch (err) {
      // A token that cannot read its own profile is not a usable session.
      api.clearSession()
      throw err
    }

    if (!mountedRef.current) return resolved
    setUser(resolved)
    setMustChangePassword(forcedRotation || resolved?.must_change_password === true)
    setStatus('authenticated')
    setError(null)
    return resolved
  }, [])

  const signOut = useCallback(async () => {
    try {
      await api.logout()
    } catch {
      // The token may already be revoked or the network down. Either way the
      // local session goes.
    }
    resetToAnonymous()
  }, [resetToAnonymous])

  const clearError = useCallback(() => setError(null), [])

  const permissions = user?.permissions ?? EMPTY_PERMISSIONS

  /** Server-supplied permission lookup. Unknown keys are always false. */
  const can = useCallback(
    (permission) => permissions?.[permission] === true,
    [permissions],
  )

  const value = useMemo(
    () => ({
      user,
      status,
      error,
      permissions,
      mustChangePassword,
      isAuthenticated: status === 'authenticated',
      can,
      signIn,
      signOut,
      clearError,
    }),
    [user, status, error, permissions, mustChangePassword, can, signIn, signOut, clearError],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Co-located with the provider deliberately: the hook and the context it reads
// are one unit, and splitting them across files to satisfy the Fast Refresh
// heuristic would make the contract harder to follow than the lost HMR is worth.
// eslint-disable-next-line react/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth() must be called inside an <AuthProvider>.')
  }
  return context
}
